import { memo, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  IThread,
  useApi,
  useAudio,
  useAuth,
  useChatMessages,
  useConfig
} from '@chainlit/react-client';

import AudioPresence from '@/components/AudioPresence';
import ButtonLink from '@/components/ButtonLink';
import { useSidebar } from '@/components/ui/sidebar';

import ApiKeys from './ApiKeys';
import ChatProfiles from './ChatProfiles';
import NewChatButton from './NewChat';
import ReadmeButton from './Readme';
import ShareButton from './Share';
import SidebarTrigger from './SidebarTrigger';
import { ThemeToggle } from './ThemeToggle';
import UserNav from './UserNav';

const Header = memo(() => {
  const { audioConnection } = useAudio();
  const navigate = useNavigate();
  const { data } = useAuth();
  const [thread, setThread] = useState<IThread | null>(null);
  const { config } = useConfig();
  const { threadId: hookThreadId } = useChatMessages();
  // prefer the id prop, fallback to the hook-provided threadId
  const { id } = useParams();
  const effectiveThreadId = id ?? hookThreadId;
//   console.log('Thread id: ', effectiveThreadId);
  const {
    data: threadData,
    isLoading,
    error
  } = useApi<IThread>(
    effectiveThreadId ? `/project/share/${effectiveThreadId}` : null,
    {
      revalidateOnFocus: false
    }
  );
  //   console.log(threadData);

  const { open, openMobile, isMobile } = useSidebar();

  const sidebarOpen = isMobile ? openMobile : open;

  const historyEnabled = data?.requireLogin && config?.dataPersistence;

  const links = config?.ui?.header_links || [];

  useEffect(() => {
    if (threadData) {
      setThread(threadData);
    } else {
      console.log('threadData is undefined');
    }
  }, [threadData, effectiveThreadId]);
  //   console.log('thread: ', thread);

  return (
    <div
      className="p-3 flex h-[60px] items-center justify-between gap-2 relative"
      id="header"
    >
      <div className="flex items-center">
        {historyEnabled ? !sidebarOpen ? <SidebarTrigger /> : null : null}
        {historyEnabled ? (
          !sidebarOpen ? (
            <NewChatButton navigate={navigate} />
          ) : null
        ) : (
          <NewChatButton navigate={navigate} />
        )}

        <ChatProfiles navigate={navigate} />
      </div>

      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 font-semibold text-foreground text-center text-xl">
        {thread && thread.metadata?.user_name
          ? `Conversation with  
            ${thread?.metadata.user_name} - ${thread?.metadata.user_email}`
          : null}
      </div>

      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
        {audioConnection === 'on' ? (
          <AudioPresence
            type="server"
            height={35}
            width={70}
            barCount={4}
            barSpacing={2}
          />
        ) : null}
      </div>

      <div />
      <div className="flex items-center gap-1">
        <ShareButton />
        <ReadmeButton />
        <ApiKeys />
        {links &&
          links.map((link, index) => (
            <ButtonLink
              key={`${link.name}-${link.url}-${index}`}
              name={link.name}
              displayName={link.display_name}
              iconUrl={link.icon_url}
              url={link.url}
            />
          ))}
        <ThemeToggle />
        <UserNav />
      </div>
    </div>
  );
});

export { Header };
